(() => {
  if (customElements.get('wc-carousel-cards')) return;

  class WcCarouselCards extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.boundResize = this.debounce(this.handleResize.bind(this), 150);
      this.copyHandler = this.handleCopyClick.bind(this);
    }

    connectedCallback() {
      this.sectionElement = this.closest('[data-wc-carousel-section]');
      this.swiperElement = this.querySelector('.wc-carousel__swiper');
      this.prevEl = this.querySelector('.wc-carousel__nav--prev');
      this.nextEl = this.querySelector('.wc-carousel__nav--next');
      this.mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint || '767', 10);
      this.enableLoop = this.dataset.loop === 'true';

      this.bindCopyButtons();

      this.waitForSwiper(() => {
        this.handleResize();
        window.addEventListener('resize', this.boundResize, { passive: true });
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundResize);
      this.unbindCopyButtons();
      this.destroySwiper();
    }

    debounce(fn, wait) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
      };
    }

    waitForSwiper(callback, retry = 0) {
      if (typeof window.Swiper === 'function') {
        callback();
        return;
      }
      if (retry > 20) return;
      setTimeout(() => this.waitForSwiper(callback, retry + 1), 120);
    }

    isMobile() {
      return window.innerWidth <= this.mobileBreakpoint;
    }

    resolveGap() {
      const raw = this.isMobile()
        ? this.dataset.spaceBetweenM
        : this.dataset.spaceBetween;
      const gap = parseInt(raw || '20', 10);
      return Number.isFinite(gap) ? gap : 20;
    }

    bindCopyButtons() {
      this.querySelectorAll('[data-wc-copy-code]').forEach((button) => {
        button.addEventListener('click', this.copyHandler);
      });
    }

    unbindCopyButtons() {
      this.querySelectorAll('[data-wc-copy-code]').forEach((button) => {
        button.removeEventListener('click', this.copyHandler);
      });
    }

    handleCopyClick(event) {
      const button = event.currentTarget;
      const code = button.dataset.wcCopyCode;
      if (!code) return;

      const copiedLabel = button.dataset.copiedLabel || 'Copied!';
      const defaultLabel = button.textContent.trim();

      const onSuccess = () => {
        button.classList.add('is-copied');
        button.textContent = copiedLabel;
        window.setTimeout(() => {
          button.classList.remove('is-copied');
          button.textContent = defaultLabel;
        }, 1800);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(onSuccess).catch(() => {});
        return;
      }

      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand('copy');
        onSuccess();
      } catch (error) {
        /* noop */
      }

      document.body.removeChild(textarea);
    }

    handleResize() {
      if (!this.swiperElement) return;

      if (this.isMobile()) {
        this.destroySwiper();
        return;
      }

      const spaceBetween = this.resolveGap();

      if (this.swiperInstance) {
        this.swiperInstance.params.spaceBetween = spaceBetween;
        this.swiperInstance.update();
        return;
      }

      this.initSwiper(spaceBetween);
    }

    initSwiper(spaceBetween) {
      if (this.swiperInstance || typeof window.Swiper !== 'function') return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const navigation = {};

      if (this.prevEl && this.nextEl) {
        navigation.prevEl = this.prevEl;
        navigation.nextEl = this.nextEl;
      }

      this.swiperInstance = new window.Swiper(this.swiperElement, {
        slidesPerView: 'auto',
        spaceBetween,
        watchOverflow: true,
        speed: prefersReducedMotion ? 0 : 600,
        loop: this.enableLoop,
        grabCursor: true,
        navigation
      });
    }

    destroySwiper() {
      if (!this.swiperInstance) return;
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  customElements.define('wc-carousel-cards', WcCarouselCards);
})();
